/**
 * Copyright 2016-2021 Sourcepole AG
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react';
import './SearchProviders'; // registers custom search providers (window.QWC2SearchProviders)
import {renderHelp} from './Help';
import ErrorBoundary, {notifyError, safeTr, withErrorBoundary} from './components/ErrorBoundary';

import MapPlugin from './overwrite/plugins/Map';
import EditingSupport from 'qwc2/plugins/map/EditingSupport';
import MeasurementSupport from 'qwc2/plugins/map/MeasurementSupport';
import LocateSupport from 'qwc2/plugins/map/LocateSupport';
import RedliningSupport from 'qwc2/plugins/map/RedliningSupport';
import SnappingSupport from 'qwc2/plugins/map/SnappingSupport';
import HomeButtonPlugin from 'qwc2/plugins/HomeButton';
import LocateButtonPlugin from 'qwc2/plugins/LocateButton';
import {ZoomInPlugin, ZoomOutPlugin} from 'qwc2/plugins/ZoomButtons';
import TaskButtonPlugin from 'qwc2/plugins/TaskButton';
import BackgroundSwitcherPlugin from './overwrite/plugins/BackgroundSwitcher';
import TopBarPlugin from 'qwc2/plugins/TopBar';
import AppMenu from 'qwc2/components/AppMenu';
import SearchBox from './overwrite/components/SearchBox';
import MapViewHistory from './plugins/MapViewHistory';
import Toolbar from 'qwc2/components/Toolbar';
import FullscreenSwitcher from 'qwc2/components/FullscreenSwitcher';
import BottomBarPlugin from 'qwc2/plugins/BottomBar';
import MeasurePlugin from 'qwc2/plugins/Measure';
import ThemeSwitcherPlugin from 'qwc2/plugins/ThemeSwitcher';
import LayerTreePlugin from './overwrite/plugins/LayerTree';
import LayerCatalogPlugin from 'qwc2/plugins/LayerCatalog';
import IdentifyPlugin from './overwrite/plugins/Identify';
import FeatureDetailsPlugin from './plugins/FeatureDetails';
import AddFeaturePlugin from './plugins/AddFeature';
import IndicatorDetailsPlugin from './plugins/IndicatorDetails';
import FloatLogoPlugin from './plugins/FloatLogo';
import MapTipPlugin from 'qwc2/plugins/MapTip';
import SharePlugin from 'qwc2/plugins/Share';
import MapCopyrightPlugin from 'qwc2/plugins/MapCopyright';
import PrintPlugin from 'qwc2/plugins/Print';
import HelpPlugin from 'qwc2/plugins/Help';
import ContactPlugin from './plugins/Contact';
import LogoutPlugin from './plugins/Logout';
import LayerFilterPlugin from './plugins/LayerFilter';
import IndicatorsPlugin from './plugins/Indicators';
import RedliningPlugin from 'qwc2/plugins/Redlining';
import BufferSupport from 'qwc2/plugins/redlining/RedliningBufferSupport';
import EditingPlugin from 'qwc2/plugins/Editing';
import MapComparePlugin from 'qwc2/plugins/MapCompare';
import HeightProfilePlugin from 'qwc2/plugins/HeightProfile';
import MapInfoTooltipPlugin from 'qwc2/plugins/MapInfoTooltip';
import StartupMarkerPlugin from 'qwc2/plugins/StartupMarker';
import ScratchDrawingPlugin from 'qwc2/plugins/ScratchDrawing';
import AuthenticationPlugin from 'qwc2/plugins/Authentication';
import BookmarkPlugin from 'qwc2/plugins/Bookmark';
import AttributeTablePlugin from 'qwc2/plugins/AttributeTable';
import APIPlugin from 'qwc2/plugins/API';
import {customAttributeCalculator} from './CustomAttributeCalculator';

import defaultLocaleData from '../static/translations/en-US.json';

// The top bar only offers fixed component slots, so the history buttons share the search slot
const SearchWithHistory = SearchBox;
const SearchSlot = (props) => (
    <>
        <ErrorBoundary fallback={null} name="MapViewHistory">
            <MapViewHistory />
        </ErrorBoundary>
        <ErrorBoundary
            fallback={null}
            name="SearchBox"
            onError={() => notifyError("SearchBox", safeTr("errorboundary.pluginfailed", "Die Suche ist ausgefallen. Der Rest der Anwendung ist weiter nutzbar.", "Die Suche"), true)}
        >
            <SearchWithHistory {...props} />
        </ErrorBoundary>
    </>
);


const appConfig = {
    defaultLocaleData: defaultLocaleData,
    initialState: {
        defaultState: {},
        mobile: {}
    },
    pluginsDef: {
        plugins: {
            MapPlugin: MapPlugin({
                EditingSupport: EditingSupport,
                MeasurementSupport: MeasurementSupport,
                LocateSupport: LocateSupport,
                RedliningSupport: RedliningSupport,
                SnappingSupport: SnappingSupport
            }),
            HomeButtonPlugin: HomeButtonPlugin,
            LocateButtonPlugin: LocateButtonPlugin,
            ZoomInPlugin: ZoomInPlugin,
            ZoomOutPlugin: ZoomOutPlugin,
            TaskButtonPlugin: TaskButtonPlugin,
            BackgroundSwitcherPlugin: BackgroundSwitcherPlugin,
            TopBarPlugin: TopBarPlugin({
                AppMenu: AppMenu,
                Search: SearchSlot,
                Toolbar: Toolbar,
                FullscreenSwitcher: FullscreenSwitcher
            }),
            LayerFilterPlugin: LayerFilterPlugin,
            IndicatorsPlugin: IndicatorsPlugin,
            BottomBarPlugin: BottomBarPlugin,
            MeasurePlugin: MeasurePlugin,
            ThemeSwitcherPlugin: ThemeSwitcherPlugin,
            LayerTreePlugin: LayerTreePlugin,
            LayerCatalogPlugin: LayerCatalogPlugin,
            IdentifyPlugin: IdentifyPlugin,
            FeatureDetailsPlugin: FeatureDetailsPlugin,
            AddFeaturePlugin: AddFeaturePlugin,
            IndicatorDetailsPlugin: IndicatorDetailsPlugin,
            FloatLogoPlugin: FloatLogoPlugin,
            MapTipPlugin: MapTipPlugin,
            SharePlugin: SharePlugin,
            MapCopyrightPlugin: MapCopyrightPlugin,
            PrintPlugin: PrintPlugin,
            LogoutPlugin: LogoutPlugin,
            HelpPlugin: HelpPlugin(renderHelp),
            ContactPlugin: ContactPlugin,
            RedliningPlugin: RedliningPlugin({
                BufferSupport: BufferSupport
            }),
            // Per default the editing interface qwc2/utils/EditingInterface.js is used
            // You can pass a custom editing interface here if desired
            EditingPlugin: EditingPlugin(/* CustomEditingInterface */),
            MapComparePlugin: MapComparePlugin,
            HeightProfilePlugin: HeightProfilePlugin,
            MapInfoTooltipPlugin: MapInfoTooltipPlugin(),
            StartupMarkerPlugin: StartupMarkerPlugin,
            ScratchDrawingPlugin: ScratchDrawingPlugin,
            AuthenticationPlugin: AuthenticationPlugin,
            BookmarkPlugin: BookmarkPlugin,
            AttributeTablePlugin: AttributeTablePlugin(),
            APIPlugin: APIPlugin
        },
        cfg: {
            IdentifyPlugin: {
                attributeCalculator: customAttributeCalculator
            }
        }
    },
    actionLogger: (action) => {
        /* Do something with action, i.e. Piwik/Mamoto event tracking */
    },
    themeLayerRestorer: (missingLayers, theme, callback) => {
        // Invoked for layers specified in the l url parameter which are missing in the specified theme
        // Could be used to query a search provider for the missing theme layers

        // A list of theme layers to merge into the theme
        let newLayers = [];

        // A dictionary mapping the name of the searched layer name with the resulting layer name(s) as an array, i.e.
        // {searchlayername: ["resultlayername1", "resultlayername2"], ...}
        let newLayerNames = {};

        callback(newLayers, newLayerNames);
    }
    /*externalLayerRestorer: (externalLayers, themes, callback) => {
        // Optional function to handle restoring of external layers from the l URL parameter
        // If omitted, the default handler is used which downloads capabilities for each service to restore the layer
    }*/
};

// A plugin that fails while rendering must not take down the whole application: every plugin is wrapped into
// an error boundary (the statics qwc2 reads from plugins are passed through by withErrorBoundary)
appConfig.pluginsDef.plugins = Object.fromEntries(
    Object.entries(appConfig.pluginsDef.plugins).map(([key, Plugin]) => [key, withErrorBoundary(Plugin, {name: key.replace(/Plugin$/, '')})])
);

export default appConfig;

